/**
 * Small, dependency-free date helpers for the custom date/time pickers.
 * Dates are handled as local calendar dates (anchored at local noon to
 * dodge DST/UTC edge cases) and serialised as `yyyy-mm-dd`.
 */

export interface CalendarCell {
  date: Date;
  iso: string;
  day: number;
  inMonth: boolean;
}

/** Local Date at noon for the given y/m(0-11)/d — TZ-safe for calendar math. */
export function localDate(year: number, month: number, day: number): Date {
  return new Date(year, month, day, 12, 0, 0, 0);
}

export function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse `yyyy-mm-dd` to a local-noon Date, or null. */
export function parseISO(s: string | undefined | null): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  const d = localDate(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function todayISO(): string {
  return toISO(new Date());
}

export function addMonths(year: number, month: number, delta: number): {
  year: number;
  month: number;
} {
  const base = new Date(year, month + delta, 1);
  return { year: base.getFullYear(), month: base.getMonth() };
}

export function sameISO(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a === b;
}

/** True when `iso` is strictly between `a` and `b` (exclusive), order-agnostic. */
export function isBetween(iso: string, a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  return iso > lo && iso < hi;
}

/**
 * A Monday-first 6-row grid (42 cells) for the given month, with leading /
 * trailing days from the neighbouring months flagged `inMonth: false`.
 */
export function monthMatrix(year: number, month: number): CalendarCell[] {
  const first = localDate(year, month, 1);
  // JS: 0=Sun … 6=Sat. Shift so Monday=0.
  const lead = (first.getDay() + 6) % 7;
  const start = localDate(year, month, 1 - lead);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = localDate(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      date: d,
      iso: toISO(d),
      day: d.getDate(),
      inMonth: d.getMonth() === month,
    });
  }
  return cells;
}

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const WEEKDAYS_MON = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
export const WEEKDAYS_MON_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Format `yyyy-mm-dd` as e.g. "5 Apr 2025" (en-AU short). */
export function formatShortDate(iso: string): string {
  const d = parseISO(iso);
  if (!d) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/* ---- time helpers (12h AM/PM) ---- */

export interface Time12 {
  hour: number; // 1..12
  minute: number; // 0..59
  ampm: 'AM' | 'PM';
}

/** Parse a 24h `hh:mm` string into 12h parts. Defaults to 9:00 AM. */
export function parse24(hhmm: string | undefined | null): Time12 {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return { hour: 9, minute: 0, ampm: 'AM' };
  let h = Number(m[1]);
  const min = Number(m[2]);
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return { hour: h, minute: min, ampm };
}

/** Serialise 12h parts back to a 24h `hh:mm` string. */
export function to24({ hour, minute, ampm }: Time12): string {
  let h = hour % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function formatTime12({ hour, minute, ampm }: Time12): string {
  return `${hour}:${String(minute).padStart(2, '0')} ${ampm}`;
}

export const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
export const MINUTES = Array.from({ length: 60 }, (_, i) => i);
export const AMPM = ['AM', 'PM'] as const;
